import React, { createContext, ReactNode, useContext, useEffect, useState } from "react";
import ApiApplicationApplicant from "@/model/interface/api/ApiApplicationApplicant";
import { useNavigate, useParams } from "react-router-dom";
import { useGetApplicationQuery } from "@/store/service";
import { ApiApplication } from "@/model/interface/api/ApiApplication";
import Skeleton from "react-loading-skeleton";
import { isDebugForms } from "@/util/debug";

interface ApplicantChecksType {
  applicantType: "main-applicant" | "co-applicant";
  applicationId: string;
  applicant?: ApiApplicationApplicant;
  application: ApiApplication;
}

const ApplicantChecksContext = createContext<ApplicantChecksType | undefined>(undefined);

interface ApplicantChecksProps {
  children: ReactNode;
}

const routeTypeToDataType: Record<string, "MainApplicant" | "CoApplicant"> = {
  "main-applicant": "MainApplicant",
  "co-applicant": "CoApplicant",
};

export const ApplicantChecksProvider = ({ children }: ApplicantChecksProps) => {
  const navigate = useNavigate();
  const params = useParams();
  const applicationId = params.id ?? "";
  const applicantType = params.type as "main-applicant" | "co-applicant";
  const isValidType = applicantType === "main-applicant" || applicantType === "co-applicant";
  const [applicant, setApplicant] = useState<ApiApplicationApplicant | undefined>();

  const { data: application, isLoading } = useGetApplicationQuery(applicationId, {
    skip: !applicationId || !isValidType,
  });

  useEffect(() => {
    if (!applicationId || !isValidType) {
      navigate("/");
    }
  }, [applicationId, isValidType]);

  useEffect(() => {
    if (application) {
      const dataType = routeTypeToDataType[applicantType];
      setApplicant(application?.applicants?.find((a) => a.type === dataType));
    }
  }, [application]);

  if (!applicationId || !isValidType || isLoading || !application) {
    return <Skeleton count={5} />;
  }

  if (!applicant && !isDebugForms()) {
    return <Skeleton count={5} />;
  }

  return (
    <ApplicantChecksContext.Provider value={{ applicant, application, applicantType, applicationId }}>
      {children}
    </ApplicantChecksContext.Provider>
  );
};
export const useApplicantChecks = (): ApplicantChecksType => {
  const context = useContext(ApplicantChecksContext);
  if (!context) {
    throw new Error("context must be used within a ApplicantChecksProvider");
  }
  return context;
};
